import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useDebouncedCallback } from 'use-debounce';
// Api
import { GET_PLACE_DETAILS, GET_PLACE_PREDICTIONS } from 'api/places/queries';
// Types
import {
  GetPlaceDetails,
  GetPlaceDetails_getPlaceDetails,
  GetPlaceDetailsVariables,
} from 'api/places/types/GetPlaceDetails';
import {
  GetPlacePredictions,
  GetPlacePredictions_getPlacePredictions_entities,
  GetPlacePredictionsVariables,
} from 'api/places/types/GetPlacePredictions';
// Hooks
import { useBreakpoints } from 'hooks';
// Helpers
import { getPlaceDisplayValue } from 'helpers/place';
// UI
import { BaseInputProps } from 'helpers/input';
import { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
import Select from 'ui3/Select/Select';
import MobileSelectSheet from 'ui3/MobileSelectSheet/MobileSelectSheet';
import MobileSelectTrigger from 'ui3/MobileSelectSheet/MobileSelectTrigger';

type PlaceOption = Pick<
  GetPlacePredictions_getPlacePredictions_entities,
  'id' | 'text'
>;

type PlaceAutocompleteProps = {
  name: string;
  value: string;
  onChange: (details: GetPlaceDetails_getPlaceDetails) => void;
  dropdownIndicator?: IconNames | null;
  /** Below the desktop breakpoint, pick the place in a full screen sheet instead of a dropdown. */
  mobileSheet?: boolean;
} & BaseInputProps;

const PlaceAutocomplete = ({
  name,
  value,
  disabled = false,
  label,
  placeholder,
  className,
  error,
  onChange,
  notice,
  hint,
  dropdownIndicator,
  mobileSheet = false,
}: PlaceAutocompleteProps) => {
  const { isBreakpointL } = useBreakpoints();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetOptions, setSheetOptions] = useState<PlaceOption[]>([]);
  const isMountedRef = useRef(true);
  const sessionTokenRef = useRef<string>(uuidv4());
  const predictionRequestIdRef = useRef(0);
  const placeDetailsRequestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<PlaceOption | null>(
    value ? { id: value, text: value } : null
  );

  useEffect(() => {
    setSelectedOption(value ? { id: value, text: value } : null);
    setInputValue('');
  }, [value]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Prepare the predictions query (skipped on mount).
  const { refetch: fetchPredictions, loading } = useQuery<
    GetPlacePredictions,
    GetPlacePredictionsVariables
  >(GET_PLACE_PREDICTIONS, {
    skip: true,
  });
  const [fetchPlaceDetails] = useLazyQuery<
    GetPlaceDetails,
    GetPlaceDetailsVariables
  >(GET_PLACE_DETAILS);

  // This callback is called when the generic autocomplete input changes.
  const debouncedInputChange = useDebouncedCallback(
    async (
      query: string,
      callback: (
        results: GetPlacePredictions_getPlacePredictions_entities[]
      ) => void
    ) => {
      const requestId = ++predictionRequestIdRef.current;

      try {
        const { data } = await fetchPredictions({
          input: {
            query,
            sessionToken: sessionTokenRef.current,
          },
        });

        if (
          !isMountedRef.current ||
          requestId !== predictionRequestIdRef.current
        ) {
          return;
        }

        const entities = data?.getPlacePredictions?.entities || [];

        callback(entities);
      } catch (error) {
        if (
          !isMountedRef.current ||
          requestId !== predictionRequestIdRef.current
        ) {
          return;
        }

        console.error('Error fetching place predictions:', error);
        callback([]);
      }
    },
    300
  );

  // The loadOptions function is what AsyncSelect calls.
  // We delegate the work to our debounced function.
  const loadOptions = (
    inputValue: string,
    callback: (
      options: GetPlacePredictions_getPlacePredictions_entities[]
    ) => void
  ) => {
    if (!inputValue) {
      predictionRequestIdRef.current += 1;
      callback([]);
      return;
    }
    debouncedInputChange(inputValue, callback);
  };

  // This callback is called when a suggestion is selected.
  const handleSelect = async (
    item: GetPlacePredictions_getPlacePredictions_entities
  ) => {
    const requestId = ++placeDetailsRequestIdRef.current;
    setSelectedOption({ id: item.id, text: item.text });
    setInputValue('');

    try {
      const { data } = await fetchPlaceDetails({
        variables: {
          input: {
            id: item.id,
            sessionToken: sessionTokenRef.current,
          },
        },
      });
      const placeDetails = data?.getPlaceDetails;
      if (
        !isMountedRef.current ||
        requestId !== placeDetailsRequestIdRef.current
      ) {
        return;
      }

      if (placeDetails && onChange) {
        const display = getPlaceDisplayValue(placeDetails);
        setSelectedOption({
          id: placeDetails.id || item.id || display,
          text: display,
        });
        onChange(placeDetails);
      }
    } catch (error) {
      if (
        !isMountedRef.current ||
        requestId !== placeDetailsRequestIdRef.current
      ) {
        return;
      }

      console.error('Error fetching place details:', error);
    }

    if (
      isMountedRef.current &&
      requestId === placeDetailsRequestIdRef.current
    ) {
      // Reset session token for the next query
      sessionTokenRef.current = uuidv4();
    }
  };

  const handleInputChange = (
    nextValue: string,
    { action }: { action: string }
  ) => {
    if (action === 'input-change') {
      setInputValue(nextValue);
    }

    if (action === 'input-blur' || action === 'menu-close') {
      setInputValue('');
    }

    return nextValue;
  };

  const getPlacePredictionOptionLabel = (
    option: GetPlacePredictions_getPlacePredictions_entities
  ) => option.text;

  const getPlacePredictionOptionValue = (
    option: GetPlacePredictions_getPlacePredictions_entities | PlaceOption
  ) => option.id;

  const showMobileSheet = mobileSheet && !isBreakpointL;

  if (showMobileSheet) {
    const closeSheet = () => {
      debouncedInputChange.cancel?.();
      predictionRequestIdRef.current += 1;
      setIsSheetOpen(false);
      setSheetOptions([]);
    };

    return (
      <>
        <MobileSelectTrigger
          name={name}
          label={label}
          placeholder={placeholder}
          valueText={selectedOption?.text}
          error={typeof error === 'string' ? error : undefined}
          disabled={disabled}
          className={className}
          onClick={() => setIsSheetOpen(true)}
        />
        <MobileSelectSheet
          open={isSheetOpen}
          title={label || 'Location'}
          options={sheetOptions}
          getOptionLabel={getPlacePredictionOptionLabel}
          getOptionValue={getPlacePredictionOptionValue}
          selectedValue={selectedOption?.id}
          searchPlaceholder="Search location"
          onSearch={(query) => loadOptions(query, setSheetOptions)}
          loading={loading}
          onSelect={(option) => {
            handleSelect(
              option as GetPlacePredictions_getPlacePredictions_entities
            );
            closeSheet();
          }}
          onClose={closeSheet}
        />
      </>
    );
  }

  return (
    <Select
      type="async"
      name={name}
      label={label}
      disabled={disabled}
      placeholder={placeholder}
      error={error}
      className={className}
      notice={notice}
      hint={hint}
      dropdownIndicator={dropdownIndicator}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      loadOptions={loadOptions}
      isLoading={loading}
      getOptionLabel={getPlacePredictionOptionLabel}
      getOptionValue={getPlacePredictionOptionValue}
    />
  );
};

export default PlaceAutocomplete;
